import React from 'react';
import Link from 'next/link';
import styles from './style.module.scss';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { generateNeighbourhoodSlug } from 'utils/generate-neighbourhood-slug';

import type { InsightsResponse, TopNeighbourhood } from '@zoocasa/go-search';
import type { SurroundingCities } from 'components/related-searches';

export interface TopNeighbourhoodsProps {
  /** The insights data containing top neighbourhoods */
  insightsData?: InsightsResponse;
  /** List of surrounding/nearby cities */
  surroundingCities?: SurroundingCities[];
  /** The name of the area for display */
  areaName?: string;
  /** Area slug for generating neighbourhood URLs */
  areaSlug?: string;
  /** Client name to personalize the heading (e.g. "Top Neighbourhoods for Joel") */
  clientName?: string;
}

interface NeighbourhoodItemProps {
  name: string;
  count?: number;
  slug?: string;
  rank: number;
  infoUrl?: string;
  realEstateUrl?: string;
}

const NeighbourhoodItem = ({ name, count, slug, rank, infoUrl, realEstateUrl }: NeighbourhoodItemProps) => {
  const { t } = useTranslation(Namespaces.COMMON);

  const hasLinks = infoUrl || realEstateUrl || slug;

  const content = (
    <div className={styles.neighbourhoodItem}>
      <span className={styles.rank}>{rank}</span>
      <div className={styles.itemContent}>
        <span className={styles.neighbourhoodName}>{name}</span>
        {count !== undefined && count > 0 && (
          <span className={styles.listingCount}>
            {t(LOCALE_COMMON_PATHS.CITYINFO_TOPNEIGHBOURHOODS_LISTINGSCOUNT, { count, defaultValue: '{{count}} listings' })}
          </span>
        )}
      </div>
      {hasLinks && (
        <div className={styles.linksContainer}>
          {infoUrl && (
            <Link href={infoUrl} className={styles.infoLink} title={t(LOCALE_COMMON_PATHS.CITYINFO_TOPNEIGHBOURHOODS_INFOLINKTITLE, { defaultValue: 'View info page' })}>
              {t(LOCALE_COMMON_PATHS.CITYINFO_TOPNEIGHBOURHOODS_INFOLINKTEXT, { defaultValue: 'Info' })}
            </Link>
          )}
          {(realEstateUrl || slug) && (
            <Link
              href={realEstateUrl || `/${slug}-real-estate`}
              className={styles.realEstateLink}
              title={t(LOCALE_COMMON_PATHS.CITYINFO_TOPNEIGHBOURHOODS_REALESTATELINKTITLE, { defaultValue: 'View real estate listings' })}
            >
              {t(LOCALE_COMMON_PATHS.CITYINFO_TOPNEIGHBOURHOODS_REALESTATELINKTEXT, { defaultValue: 'Listings' })}
            </Link>
          )}
        </div>
      )}
    </div>
  );

  return content;
};

/**
 * Extracts province/state abbreviation from a slug (e.g., "toronto-on" -> "ON")
 */
const getProvinceFromSlug = (slug?: string): string | undefined => {
  if (!slug) return undefined;
  const parts = slug.split('-');
  if (parts.length >= 2) {
    const lastPart = parts[parts.length - 1];
    // Province/state abbreviations are typically 2 characters
    if (lastPart.length === 2) {
      return lastPart.toUpperCase();
    }
  }
  return undefined;
};

/**
 * Gets the display name for a city, including province if available
 */
const getCityDisplayName = (city: SurroundingCities): string => {
  const province = city.province || getProvinceFromSlug(city.slug);
  return province ? `${city.city}, ${province}` : city.city;
};

/**
 * Capitalizes the first letter of each word in a name.
 */
const capitalizeName = (name: string): string =>
  name.replace(/\b\w/g, char => char.toUpperCase());

/**
 * Displays top neighbourhoods within an area and nearby cities.
 */
const TopNeighbourhoods = ({ insightsData, surroundingCities, areaName, areaSlug, clientName }: TopNeighbourhoodsProps) => {
  const { t } = useTranslation(Namespaces.COMMON);

  const topNeighbourhoods = insightsData?.topNeighbourhoods || [];
  const hasNeighbourhoods = topNeighbourhoods.length > 0;
  const hasSurroundingCities = surroundingCities && surroundingCities.length > 0;

  if (!hasNeighbourhoods && !hasSurroundingCities) {
    return null;
  }

  return (
    <section className={styles.component}>
      <div className={styles.columnsContainer}>
        {hasNeighbourhoods && (
          <div className={styles.column}>
            <h2 className={styles.heading}>
              {clientName
                ? t(LOCALE_COMMON_PATHS.CITYINFO_TOPNEIGHBOURHOODS_HEADINGFORCLIENT, { name: capitalizeName(clientName), defaultValue: 'Top Neighbourhoods for {{name}}' })
                : areaName
                  ? t(LOCALE_COMMON_PATHS.CITYINFO_TOPNEIGHBOURHOODS_HEADINGWITHAREA, { areaName, defaultValue: 'Top Neighbourhoods in {{areaName}}' })
                  : t(LOCALE_COMMON_PATHS.CITYINFO_TOPNEIGHBOURHOODS_HEADING, { defaultValue: 'Top Neighbourhoods' })}
            </h2>
            <p className={styles.description}>
              {t(LOCALE_COMMON_PATHS.CITYINFO_TOPNEIGHBOURHOODS_DESCRIPTION, { defaultValue: 'Most popular neighbourhoods based on active listings' })}
            </p>
            <div className={styles.list}>
              {topNeighbourhoods.slice(0, 10).map((neighbourhood: TopNeighbourhood, index: number) => {
                const neighbourhoodSlug = generateNeighbourhoodSlug(neighbourhood.name);
                const infoUrl = areaSlug ? `/${areaSlug}-info/${neighbourhoodSlug}/` : undefined;
                const realEstateUrl = areaSlug ? `/${areaSlug}-real-estate/${neighbourhoodSlug}/` : undefined;
                
                return (
                  <NeighbourhoodItem
                    key={neighbourhood.name}
                    name={neighbourhood.name}
                    count={neighbourhood.count}
                    rank={index + 1}
                    infoUrl={infoUrl}
                    realEstateUrl={realEstateUrl}
                  />
                );
              })}
            </div>
          </div>
        )}

        {hasSurroundingCities && (
          <div className={styles.column}>
            <h2 className={styles.heading}>{t(LOCALE_COMMON_PATHS.CITYINFO_TOPNEIGHBOURHOODS_NEARBYCITIESHEADING, { defaultValue: 'Nearby Cities' })}</h2>
            <p className={styles.description}>
              {t(LOCALE_COMMON_PATHS.CITYINFO_TOPNEIGHBOURHOODS_NEARBYCITIESDESCRIPTION, { defaultValue: 'Explore real estate in surrounding areas' })}
            </p>
            <div className={styles.list}>
              {surroundingCities.slice(0, 10).map((city: SurroundingCities, index: number) => (
                <NeighbourhoodItem
                  key={city.slug || city.city}
                  name={getCityDisplayName(city)}
                  slug={city.slug}
                  rank={index + 1}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TopNeighbourhoods;
